import React, { useEffect, useState, useCallback } from 'react';
import authConfig from '../authConfig';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import msalInstance, { initializeMsal } from '../msalInstance';
import LoadingSpinner from './LoadingSpinner';
import QRCard from './QRCard';
import mqtt from "mqtt";
import CountdownProgress from './CountdownProgress';
import LogOutButton from './LogOutButton';
import CheckIcon from './CheckIcon';

const apiUrl = authConfig[process.env.REACT_APP_ENTRA_ENVIRONMENT].apiUrl || `${window.location.origin}`;


const VALID_STATE = {
  "LOGIN" : "login",
  "SCANNED" : 'scanned',
  "LOGGED-IN" : 'logged-in'
};

const Callback = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [qrCode, setQrCode] = useState('');
  const [mqttClient, setMqttClient] = useState(null);
  const [topic, setTopic] = useState('');
  const [isConnectedToTopic, setIsConnectedToTopic] = useState(true);
  const [countdown, setCountdown] = useState(120);
  const [initialTime, setInitialTime] = useState(120);
  const [currentState, setCurrentState] = useState(VALID_STATE.LOGIN);

  // Memoize the handleLogoutProcess function
  const handleLogoutProcess = useCallback(async () => {
    if (mqttClient && mqttClient.connected) {
      if (topic) {
        mqttClient.unsubscribe(topic, (err) => {
          if (err) {
            console.error(`Failed to unsubscribe from topic: ${topic}`, err);
          } else {
            console.log(`Unsubscribed from topic: ${topic}`);
          }
        });
      }

      mqttClient.end(() => {
        console.log("MQTT client disconnected during logout.");
      });
    }

    // Clear component state
    setQrCode('');
    setTopic('');
    setMqttClient(null);
    setIsConnectedToTopic(false);
    setCountdown(120);
    setInitialTime(120);
    setCurrentState(VALID_STATE.LOGIN);

    // Clear all MSAL cache and logout locally (no redirect to Azure)
    await initializeMsal;

    // Clear all accounts from cache
    const accounts = msalInstance.getAllAccounts();
    accounts.forEach((account) => {
      // Remove the account from cache completely
      const accountKey = msalInstance.getAccountByHomeId(account.homeAccountId);
      if (accountKey) {
        // Clear tokens and account data
        msalInstance.setActiveAccount(null);
      }
    });

    // Clear the browser storage completely
    sessionStorage.clear();
    localStorage.clear();

    // Use replace instead of navigate to prevent going back
    window.location.replace(window.location.origin);
  }, [mqttClient, topic, navigate]);



  useEffect(() => {
    // Handle the redirect response from MSAL
    const handleAuthRedirect = async () => {
      try {
        // Ensure MSAL is initialized
        await initializeMsal;

        // Handle the redirect response
        const response = await msalInstance.handleRedirectPromise();

        if (!response) {
          // Not returning from a redirect, navigate back to login
          console.log('No redirect response, navigating to login');
          navigate('/login', { state: 'Authentication required' });
          return;
        }

        console.log('MSAL authentication successful');

        // Extract tokens from MSAL response
        const accessToken = response.accessToken;
        const idToken = response.idToken;

        // Get userinfo endpoint from MSAL account
        const userInfoUrl = `https://graph.microsoft.com/v1.0/me`;

        // Call the server with the tokens
        const postResult = await axios.post(`${apiUrl}/api/generateQrCodeWithToken`, {
          accessToken: accessToken,
          idToken: idToken,
          userInfoUrl: userInfoUrl,
          currentTime: Date.now()
        });

        const result = postResult.data;
        setQrCode(result.qrData);

        // Set the topic
        const newTopic = `${result.orgId}/authwear-${result.uid}`;
        setTopic(newTopic);

        // Get MQTT configuration
        const config = await axios.get(`${apiUrl}/api/getMqttConfiguration`);

        const client = mqtt.connect(config.data.mqttServer, {
          username: config.data.mqttUsername,
          password: config.data.mqttPassword,
          clean: true,
          protocolId: 'MQTT',
        });

        setMqttClient(client); // Set the client
      } catch (error) {
        console.error("Error in authentication flow:", error);
        navigate('/login', { state: 'Authentication failed. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthRedirect();
  }, [navigate]); // Only run once or when navigate changes
  
  // Separate effect for MQTT subscription logic
  useEffect(() => {
    if (!mqttClient || !topic) return;
  
    const handleConnect = () => {
      console.log(`Connected to MQTT broker with topic - ${topic}`);
      mqttClient.subscribe(topic, (err) => {
        if (err) {
          console.error("Subscription error:", err);
        } else {
          console.log(`Subscribed to topic: ${topic}`);
          setIsConnectedToTopic(true);
        }
      });
    };
  
    const handleMessage = (receivedTopic, message) => {

      if (receivedTopic === topic) {

        console.log(`Message from topic - ${message}`);
        const msg = JSON.parse(message);

        if (msg.authStatus === "scanned") {
          console.log("QR code scanned");
          setCountdown(30);
          setInitialTime(30);
          setCurrentState(VALID_STATE.SCANNED);
        } 
        else if (msg.authStatus === "complete") {
          console.log("Login completed");
          setCountdown(3);
          setInitialTime(3);
          setCurrentState(VALID_STATE['LOGGED-IN']);
        } 
        else if (msg.authStatus === "error") {
          console.log(`Error : ${msg}`);
          setCountdown(3);
        }
      }
    };
  
    const handleError = (err) => {
      console.error("MQTT error:", err);
    };
  
    mqttClient.on("connect", handleConnect);
    mqttClient.on("message", handleMessage);
    mqttClient.on("error", handleError);
  
    return () => {
      if (mqttClient) {
        mqttClient.unsubscribe(topic, () => {
          console.log(`Unsubscribed from topic: ${topic}`);
        });
        mqttClient.end(() => {
          console.log("MQTT client disconnected.");
        });
      }
    };
  }, [mqttClient, topic]); // Reacts only to mqttClient and topic changes

  

  useEffect(() => {
    if (isConnectedToTopic && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (countdown === 0) {
      handleLogoutProcess();
    }
  }, [isConnectedToTopic, countdown, handleLogoutProcess]);

  const isUserLogging = [VALID_STATE.SCANNED, VALID_STATE["LOGGED-IN"]].includes(currentState);

  return (
    <div className="w-full text-[#191C20] qr-card-container">
      <QRCard
        showWhen={!isUserLogging && isConnectedToTopic && !isLoading}
        value={qrCode}
        countdown={countdown}
      />
      <CheckIcon
        showWhen={!isLoading && isUserLogging}
      />
      <CountdownProgress
        initialTime={initialTime}
        remainingTime={countdown}
        showWhen={!isLoading}
        showMessage={!isUserLogging} />

      {!isLoading && isUserLogging && <div className='text-2xl font-semibold' >
        {
          (currentState === VALID_STATE.SCANNED) ? <>Scan Successful!</> : <>Authentication completed</>
        }
      </div>
      }
      <LoadingSpinner showWhen={isLoading} /> 
      <LogOutButton onClick={() => handleLogoutProcess()}/>
    </div>
  );
};

export default Callback;
